/**
 * BackupScheduleForm — Batch #14.338 : planification (fréquence, heure locale, jour), rétention, fichiers, mot de passe.
 */
import { useEffect, useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Save } from "lucide-react";

const DAYS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300";

export default function BackupScheduleForm({ settings, onSaved }) {
  const [f, setF] = useState({});
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF({ enabled: settings.enabled, frequency: settings.frequency, time: settings.time, weekday: settings.weekday ?? 0,
      retention_daily: settings.retention_daily, retention_monthly: settings.retention_monthly, include_files: settings.include_files !== false });
  }, [settings]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (password && password.length < 8) { toast.error("Mot de passe : 8 caractères minimum"); return; }
    setBusy(true);
    try {
      const r = await api.put("/admin/backup/settings", { ...f, weekday: Number(f.weekday), retention_daily: Number(f.retention_daily), retention_monthly: Number(f.retention_monthly), ...(password ? { password } : {}) });
      setPassword(""); onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="backup-schedule">
      <h3 className="text-sm font-semibold text-gray-700">Planification et sécurité</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="text-xs text-slate-600 col-span-2 md:col-span-4 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!f.enabled} onChange={(e) => set("enabled", e.target.checked)} className="w-4 h-4" data-testid="backup-enabled" />
          <span className="text-sm text-slate-800 font-medium">Sauvegarde automatique activée</span>
        </label>
        <label className="text-xs text-slate-600">Fréquence
          <select value={f.frequency || "daily"} onChange={(e) => set("frequency", e.target.value)} className={inputCls} data-testid="backup-frequency">
            <option value="daily">Chaque jour</option><option value="twice">Deux fois par jour (+12 h)</option><option value="weekly">Chaque semaine</option>
          </select>
        </label>
        <label className="text-xs text-slate-600">Heure (Québec)
          <input type="time" value={f.time || "03:00"} onChange={(e) => set("time", e.target.value)} className={inputCls} data-testid="backup-time" />
        </label>
        {f.frequency === "weekly" && <label className="text-xs text-slate-600">Jour
          <select value={f.weekday ?? 0} onChange={(e) => set("weekday", e.target.value)} className={inputCls} data-testid="backup-weekday">
            {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        </label>}
        <label className="text-xs text-slate-600">Quotidiennes conservées
          <input type="number" min="1" max="365" value={f.retention_daily ?? 30} onChange={(e) => set("retention_daily", e.target.value)} className={inputCls} data-testid="backup-retention-daily" />
        </label>
        <label className="text-xs text-slate-600">Mensuelles conservées
          <input type="number" min="1" max="120" value={f.retention_monthly ?? 12} onChange={(e) => set("retention_monthly", e.target.value)} className={inputCls} data-testid="backup-retention-monthly" />
        </label>
        <label className="text-xs text-slate-600 flex items-end gap-2 pb-2 cursor-pointer">
          <input type="checkbox" checked={f.include_files !== false} onChange={(e) => set("include_files", e.target.checked)} className="w-4 h-4" data-testid="backup-include-files" /> Inclure les fichiers stockés dans l'app
        </label>
      </div>
      <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
        <label className="text-xs text-slate-600">Mot de passe de l'archive {settings.password_set ? <span className="ml-1 inline-block px-1.5 rounded bg-emerald-100 text-emerald-800" data-testid="backup-password-set">défini</span> : <span className="ml-1 inline-block px-1.5 rounded bg-amber-100 text-amber-800" data-testid="backup-password-missing">à définir</span>}
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={settings.password_set ? "Laisser vide pour conserver le mot de passe actuel" : "8 caractères minimum — notez-le, il sert à restaurer"} className={inputCls} autoComplete="new-password" data-testid="backup-password" />
          <span className="text-[11px] text-slate-500">L'archive dans le Drive est chiffrée (AES-256) : sans ce mot de passe, personne ne peut la lire — pas même nous. Conservez-le en lieu sûr.</span>
        </label>
        <button onClick={save} disabled={busy} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-indigo-700 disabled:opacity-50" data-testid="backup-save">
          <Save className="w-4 h-4" /> Enregistrer
        </button>
      </div>
    </div>
  );
}
